import React, { useState, useEffect, useCallback } from 'react';
import Styles from './Step5ContentTraining.module.scss';
import { FiBook, FiActivity, FiTarget, FiBarChart2, FiSettings, FiChevronRight, FiZap, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import onBoardingService from 'services/onBoardingService';
import contentTrainingService from 'services/contentTrainingService';

// Sub-components (tabs)
import ContentLibrary from './tabs/ContentLibrary/ContentLibrary';
import VoiceDNA from './tabs/VoiceDNA/VoiceDNA';
import RefineVoice from './tabs/RefineVoice/RefineVoice';
import GapsRecommendations from './tabs/GapsRecommendations/GapsRecommendations';
import ContentStrategy from './tabs/ContentStrategy/ContentStrategy';

const TABS = [
  { id: 'library', label: 'Content Library', icon: FiBook, description: 'All your training content' },
  { id: 'voice-dna', label: 'Voice DNA', icon: FiActivity, description: 'Your brand voice analysis' },
  { id: 'refine', label: 'Refine Voice', icon: FiTarget, description: 'Teach the AI your preferences' },
  { id: 'gaps', label: 'Gaps & Recs', icon: FiBarChart2, description: 'What to add next' },
  { id: 'strategy', label: 'Content Strategy', icon: FiSettings, description: 'Goals and preferences' },
];

const Step5ContentTraining = ({ data, onUpdate, onNext, onPrevious, brandId, onSaveAndExit, onSaveAndContinue }) => {
  const [activeTab, setActiveTab] = useState('library');
  const [isLoading, setIsLoading] = useState(true);
  const [trainingData, setTrainingData] = useState({
    contentSamples: [],
    voiceDNA: null,
    gapsAnalysis: null,
    trainingStatus: null,
  });
  const [stats, setStats] = useState({
    total: 0,
    byPlatform: {},
    byType: {},
    avgQuality: 0,
    readinessScore: 0,
    embeddedCount: 0,
  });

  // Load all content samples and training data
  const loadTrainingData = useCallback(async () => {
    if (!brandId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch all content samples
      const samplesResponse = await onBoardingService.listContentSamples(brandId);
      const samples = samplesResponse.data.success ? samplesResponse.data.data : [];

      // Calculate stats
      const byPlatform = {};
      const byType = {};
      let totalQuality = 0;
      let qualityCount = 0;

      samples.forEach(sample => {
        // By platform
        const platform = sample.platform || 'upload';
        byPlatform[platform] = (byPlatform[platform] || 0) + 1;

        // By type
        const type = sample.content_type || 'other';
        byType[type] = (byType[type] || 0) + 1;

        // Quality
        if (sample.quality_score) {
          totalQuality += sample.quality_score;
          qualityCount++;
        }
      });

      const avgQuality = qualityCount > 0 ? (totalQuality / qualityCount * 100).toFixed(0) : 0;
      const readinessScore = calculateReadinessScore(samples, byPlatform);

      setStats({
        total: samples.length,
        byPlatform,
        byType,
        avgQuality,
        readinessScore,
        embeddedCount: samples.filter(s => s.approved).length,
      });

      setTrainingData(prev => ({
        ...prev,
        contentSamples: samples,
      }));

      // Try to load voice DNA analysis if available
      try {
        const insightsResponse = await contentTrainingService.getVoiceDNA(brandId);
        if (insightsResponse.data.success) {
          setTrainingData(prev => ({
            ...prev,
            voiceDNA: insightsResponse.data.data,
          }));
        }
      } catch (e) {
        // Voice DNA not available yet, that's okay
      }

      // Load training status
      try {
        const statusResponse = await contentTrainingService.getTrainingStatus(brandId);
        if (statusResponse.data.success) {
          setTrainingData(prev => ({
            ...prev,
            trainingStatus: statusResponse.data.data,
          }));
        }
      } catch (e) {
        // Status not available
      }

    } catch (error) {
      console.error('Error loading training data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    loadTrainingData();
  }, [loadTrainingData]);

  const calculateReadinessScore = (samples, byPlatform) => {
    // Scoring criteria:
    // - Min 10 samples: +20%
    // - Min 25 samples: +20%
    // - Min 50 samples: +20%
    // - At least 2 platforms: +15%
    // - At least 3 platforms: +15%
    // - Has social content: +5%
    // - Has blog content: +5%

    let score = 0;
    const total = samples.length;
    const platformCount = Object.keys(byPlatform).length;

    if (total >= 10) score += 20;
    if (total >= 25) score += 20;
    if (total >= 50) score += 20;
    if (platformCount >= 2) score += 15;
    if (platformCount >= 3) score += 15;
    if (byPlatform.twitter || byPlatform.linkedin || byPlatform.instagram) score += 5;
    if (byPlatform.website) score += 5;

    return Math.min(100, score);
  };

  const getReadinessLabel = (score) => {
    if (score >= 80) return { text: 'Excellent', color: 'green' };
    if (score >= 60) return { text: 'Good', color: 'teal' };
    if (score >= 40) return { text: 'Getting There', color: 'orange' };
    return { text: 'Needs Content', color: 'red' };
  };

  const readinessLabel = getReadinessLabel(stats.readinessScore);

  const handleContinue = () => {
    // Pass current data for consistency (content is saved via separate APIs)
    if (onSaveAndContinue) {
      onSaveAndContinue(data);
    } else {
      onNext();
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'library':
        return (
          <ContentLibrary
            brandId={brandId}
            samples={trainingData.contentSamples}
            stats={stats}
            onRefresh={loadTrainingData}
            data={data}
            onUpdate={onUpdate}
          />
        );
      case 'voice-dna':
        return (
          <VoiceDNA
            brandId={brandId}
            voiceDNA={trainingData.voiceDNA}
            samples={trainingData.contentSamples}
            onRefresh={loadTrainingData}
          />
        );
      case 'refine':
        return (
          <RefineVoice
            brandId={brandId}
            samples={trainingData.contentSamples}
            onRefresh={loadTrainingData}
          />
        );
      case 'gaps':
        return (
          <GapsRecommendations
            brandId={brandId}
            samples={trainingData.contentSamples}
            stats={stats}
            onRefresh={loadTrainingData}
            selectedPlatforms={(data.socialChannels || []).map(ch => ch.platform)}
          />
        );
      case 'strategy':
        return (
          <ContentStrategy
            brandId={brandId}
            strategy={data?.content_strategy}
            voiceDNA={trainingData.voiceDNA}
            samples={trainingData.contentSamples}
            onRefresh={loadTrainingData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={Styles.container}>
      {/* Header Section */}
      <div className={Styles.header}>
        <div className={Styles.headerContent}>
          <div className={Styles.titleSection}>
            <h2 className={Styles.title}>Content Training</h2>
            <p className={Styles.subtitle}>
              Train your AI with your content to match your unique voice and style
            </p>
          </div>
        </div>

        {/* Training Status Card */}
        <div className={Styles.statusCard}>
          <div className={Styles.statusMain}>
            <div className={Styles.readinessGauge}>
              <div className={Styles.gaugeCircle}>
                <svg viewBox="0 0 36 36" className={Styles.circularChart}>
                  <path
                    className={Styles.circleBg}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className={`${Styles.circle} ${Styles[readinessLabel.color]}`}
                    strokeDasharray={`${stats.readinessScore}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className={Styles.gaugeValue}>{stats.readinessScore}%</div>
              </div>
              <div className={Styles.gaugeLabel}>
                <span className={`${Styles.readinessText} ${Styles[readinessLabel.color]}`}>
                  {readinessLabel.text}
                </span>
                <span className={Styles.readinessSubtext}>AI Readiness</span>
              </div>
            </div>

            <div className={Styles.statusDivider} />

            <div className={Styles.statsGrid}>
              <div className={Styles.statItem}>
                <span className={Styles.statValue}>{stats.total}</span>
                <span className={Styles.statLabel}>Total Samples</span>
              </div>
              <div className={Styles.statItem}>
                <span className={Styles.statValue}>{Object.keys(stats.byPlatform).length}</span>
                <span className={Styles.statLabel}>Sources</span>
              </div>
              <div className={Styles.statItem}>
                <span className={Styles.statValue}>{stats.avgQuality}%</span>
                <span className={Styles.statLabel}>Avg Quality</span>
              </div>
              <div className={Styles.statItem}>
                <span className={Styles.statValue}>{stats.embeddedCount}</span>
                <span className={Styles.statLabel}>Trained</span>
              </div>
            </div>
          </div>

          {stats.readinessScore < 60 && (
            <div className={Styles.statusTip}>
              <FiAlertCircle className={Styles.tipIcon} />
              <span>Add more content samples to improve AI accuracy. Aim for 25+ samples across multiple platforms.</span>
            </div>
          )}

          {stats.readinessScore >= 80 && (
            <div className={`${Styles.statusTip} ${Styles.successTip}`}>
              <FiCheckCircle className={Styles.tipIcon} />
              <span>Great job! Your AI has enough content to generate accurate brand-aligned content.</span>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={Styles.tabNavigation}>
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              className={`${Styles.tabButton} ${isActive ? Styles.active : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon className={Styles.tabIcon} />
              <div className={Styles.tabText}>
                <span className={Styles.tabLabel}>{tab.label}</span>
                <span className={Styles.tabDescription}>{tab.description}</span>
              </div>
              {isActive && <div className={Styles.activeIndicator} />}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className={Styles.tabContent}>
        {isLoading ? (
          <div className={Styles.loadingState}>
            <div className={Styles.loadingSpinner} />
            <p>Loading training data...</p>
          </div>
        ) : (
          renderTabContent()
        )}
      </div>

      {/* Footer */}
      <div className={Styles.footer}>
        <button className={Styles.previousBtn} onClick={onPrevious}>
          Previous
        </button>
        <button className={Styles.saveExitBtn} onClick={onSaveAndExit}>
          Save & Exit
        </button>
        <div className={Styles.stepIndicator}>Step 5 of 7</div>
        <button className={Styles.continueBtn} onClick={handleContinue}>
          Save & Continue
          <FiChevronRight />
        </button>
      </div>
    </div>
  );
};

export default Step5ContentTraining;
